import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// Pages
import LandingPage from "./pages/LandingPage";
import Questionnaire from "./pages/Questionnaire";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import CheckIn from "./pages/CheckIn";
import InstallPWA from "./components/InstallPWA";
import AuthModal from "./components/AuthModal";

// Maya Manthan Pages
import MayaManthanHome from "./pages/MayaManthanHome";
import MayaManthanAssessment from "./pages/MayaManthanAssessment";
import MayaManthanCourses from "./pages/MayaManthanCourses";
import MayaManthanWisdom from "./pages/MayaManthanWisdom";
import MayaManthanPath from "./pages/MayaManthanPath";
import MayaManthanJournal from "./pages/MayaManthanJournal";
import MayaManthanGuide from "./pages/MayaManthanGuide";
import MayaManthanSettings from "./pages/MayaManthanSettings";
import BadgeGallery from "./pages/BadgeGallery";
import Leaderboard from "./pages/Leaderboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// Add request interceptor to include session token from localStorage as fallback
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
    console.log('[Auth] Adding token to request:', config.url);
  } else {
    console.log('[Auth] No token found for request:', config.url);
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Check for password reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset_token');
    if (token) {
      setResetToken(token);
      setShowAuthModal(true);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const openAuthModal = () => {
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
    setResetToken(null);
    // Clear reset token from URL if present
    if (window.location.search.includes('reset_token')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuthModal(false);
    setResetToken(null);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      // Clear localStorage session token
      localStorage.removeItem('session_token');
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local state even if API call fails
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Refresh user error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      loading, 
      login, 
      logout, 
      refreshUser,
      showAuthModal,
      openAuthModal,
      closeAuthModal,
      handleAuthSuccess
    }}>
      {children}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        onSuccess={handleAuthSuccess}
        onGoogleLogin={login}
        resetToken={resetToken}
      />
    </AuthContext.Provider>
  );
}

// Auth Callback Component
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');

      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        setUser(response.data.user);
        
        // Clear hash and navigate
        window.history.replaceState(null, '', '/dashboard');
        
        if (response.data.user.has_completed_assessment) {
          navigate('/dashboard', { replace: true, state: { user: response.data.user } });
        } else {
          navigate('/questionnaire', { replace: true, state: { user: response.data.user } });
        }
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate('/');
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-cosmic-bg-primary flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center"
      >
        <div className="w-16 h-16 border-4 border-cosmic-accent-gold/30 border-t-cosmic-accent-gold rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-cosmic-text-secondary">Authenticating...</p>
      </motion.div>
    </div>
  );
}

// Protected Route
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // If user passed from AuthCallback, skip check
    if (location.state?.user) return;
    
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-bg-primary flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cosmic-accent-gold/30 border-t-cosmic-accent-gold rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentUser = location.state?.user || user;
  
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && currentUser.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/questionnaire" element={
          <ProtectedRoute>
            <Questionnaire />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/check-in" element={
          <ProtectedRoute>
            <CheckIn />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        {/* Maya Manthan Routes */}
        <Route path="/maya-manthan" element={
          <ProtectedRoute>
            <MayaManthanHome />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/assessment" element={
          <ProtectedRoute>
            <MayaManthanAssessment />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/courses" element={
          <ProtectedRoute>
            <MayaManthanCourses />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/wisdom" element={
          <ProtectedRoute>
            <MayaManthanWisdom />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/path" element={
          <ProtectedRoute>
            <MayaManthanPath />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/journal" element={
          <ProtectedRoute>
            <MayaManthanJournal />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/guide" element={
          <ProtectedRoute>
            <MayaManthanGuide />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/settings" element={
          <ProtectedRoute>
            <MayaManthanSettings />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/badges" element={
          <ProtectedRoute>
            <BadgeGallery />
          </ProtectedRoute>
        } />
        <Route path="/maya-manthan/leaderboard" element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <InstallPWA />
          <Toaster 
            position="top-center" 
            toastOptions={{
              style: {
                background: '#0F0B1E',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#F4F0FF',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API, BACKEND_URL };
